import { useState } from "react";
import { toast } from "sonner";
import { ComposedChart, LineChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChatCircle, PaperPlaneRight } from "@phosphor-icons/react";
import { useApi, errMsg, nzDate, nzDateTime, num } from "@/lib/api";
import { Btn, Pill, Empty, Modal, Section, useLoad } from "@/components/app/common";

const STATUS_TONE = { active: "green", departed: "slate", sold: "blue", dead: "red" };
export const AnimalStatus = ({ s }) => <Pill tone={STATUS_TONE[s] || "slate"}>{s}</Pill>;
export const animalLabel = (a) => a.tag || a.eid || "—";

export function WeightChart({ sessions, testid = "weight-chart" }) {
  if (!sessions?.length) return <Empty testid={`${testid}-empty`}>No weighings yet.</Empty>;
  const data = sessions.map((s) => ({ ...s, label: nzDate(s.date) }));
  return (
    <div className="h-72" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="w" tick={{ fontSize: 11 }} domain={["auto", "auto"]} unit=" kg" width={64} />
          <YAxis yAxisId="g" orientation="right" tick={{ fontSize: 11 }} unit=" kg/d" width={64} />
          <Tooltip formatter={(v, k) => (k === "Average weight" ? [`${num(v, 1)} kg`, k] : [`${num(v, 2)} kg/day`, k])} />
          <Legend formatter={(v) => <span className="text-slate-700">{v}</span>} />
          <Bar yAxisId="g" dataKey="adg" name="Daily gain since last weigh" fill="#4ade80" />
          <Line yAxisId="w" dataKey="avg_weight" name="Average weight" stroke="#166534" strokeWidth={2.5} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function StockSummary({ summary }) {
  const tiles = [["Active head", summary.active_head, ""], ["Avg latest weight", summary.avg_last_weight, " kg"],
    ["Avg daily gain (since first weigh)", summary.avg_adg_overall, " kg/day"], ["Last weigh", summary.last_session ? nzDate(summary.last_session.date) : null, ""]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stock-summary">
      {tiles.map(([l, v, u]) => <div key={l} className="panel p-4"><p className="label-sm">{l}</p><p className="text-2xl font-bold mt-1 font-mono-num">{v ?? "—"}{v != null ? u : ""}</p></div>)}
    </div>
  );
}

function AnimalWeights({ base, animal, onClose }) {
  const api = useApi();
  const [d] = useLoad(() => api.get(`${base}/animals/${animal.id}/weights`), [animal.id]);
  return (
    <Modal open title={`Animal ${animalLabel(animal)}`} onClose={onClose} wide testid="animal-weights-modal">
      <p className="text-xs text-slate-500 mb-3">EID {animal.eid || "—"} · NAIT {animal.nait || "—"} · {animal.breed || "breed not recorded"}</p>
      {!d ? <p className="text-sm">Loading…</p> : d.weights.length === 0 ? <Empty>No weighings for this animal yet.</Empty> : (
        <div className="h-64"><ResponsiveContainer width="100%" height="100%"><LineChart data={d.weights.map((w) => ({ ...w, label: nzDate(w.date) }))}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} unit=" kg" width={64} />
          <Tooltip formatter={(v) => [`${v} kg`, "Weight"]} /><Line dataKey="weight" stroke="#166534" strokeWidth={2.5} /></LineChart></ResponsiveContainer></div>)}
    </Modal>
  );
}

export function AnimalTable({ base, animals, actions }) {
  const [show, setShow] = useState("active");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const list = animals.filter((a) => (show === "all" || a.status === show) && (!q || `${a.tag} ${a.eid} ${a.nait}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <input className="field-input max-w-xs" placeholder="Search tag, EID or NAIT" value={q} onChange={(e) => setQ(e.target.value)} data-testid="animal-search" />
        <select className="field-input max-w-[160px]" value={show} onChange={(e) => setShow(e.target.value)} data-testid="animal-status-filter">
          <option value="active">Active</option><option value="all">All animals</option><option value="departed">Departed</option><option value="sold">Sold</option><option value="dead">Dead</option>
        </select>
        <span className="text-xs text-slate-500 self-center" data-testid="animal-count">{list.length} shown</span>
      </div>
      {list.length === 0 ? <Empty testid="animals-empty">No animals to show.</Empty> : (
        <div className="overflow-x-auto max-h-[480px]"><table className="w-full text-sm" data-testid="animal-table">
          <thead className="sticky top-0 bg-white"><tr className="text-left border-b border-slate-200">{["Tag / EID", "Status", "First weight", "Latest weight", "Gain", "ADG last", "ADG overall", ""].map((h) => <th key={h} className="py-2 pr-3 label-sm whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{list.map((a) => (
            <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`animal-row-${a.id}`}>
              <td className="py-2 pr-3"><button className="font-medium hover:underline text-left" onClick={() => setOpen(a)} data-testid={`animal-open-${a.id}`}>{animalLabel(a)}</button><span className="block text-xs text-slate-500 font-mono-num">{a.tag ? a.eid : a.nait}</span></td>
              <td className="pr-3"><AnimalStatus s={a.status} /></td>
              <td className="pr-3 font-mono-num">{a.first_weight != null ? `${a.first_weight} kg` : "—"}<span className="block text-xs text-slate-500">{a.first_date ? nzDate(a.first_date) : ""}</span></td>
              <td className="pr-3 font-mono-num">{a.last_weight != null ? `${a.last_weight} kg` : "—"}<span className="block text-xs text-slate-500">{a.last_date ? nzDate(a.last_date) : ""}</span></td>
              <td className="pr-3 font-mono-num">{a.gain != null ? `${a.gain > 0 ? "+" : ""}${a.gain} kg` : "—"}</td>
              <td className="pr-3 font-mono-num">{a.adg_last != null ? num(a.adg_last, 2) : "—"}</td>
              <td className="pr-3 font-mono-num">{a.adg_overall != null ? num(a.adg_overall, 2) : "—"}</td>
              <td className="text-right">{actions?.(a)}</td>
            </tr>))}</tbody>
        </table></div>)}
      {open && <AnimalWeights base={base} animal={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

export function CommentThread({ base, mobId, animals = [] }) {
  const api = useApi();
  const [items, reload] = useLoad(() => api.get(`${base}/comments?mob_id=${mobId}`), [mobId]);
  const [text, setText] = useState("");
  const [animalId, setAnimalId] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`${base}/comments`, { mob_id: mobId, animal_id: animalId || null, text }); setText(""); setAnimalId(""); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Section title="Comments" testid="comment-thread" actions={<ChatCircle size={20} className="text-slate-400" />}>
      {!items ? <p className="text-sm">Loading…</p> : items.length === 0 ? <Empty testid="comments-empty">No comments yet.</Empty> : (
        <ul className="space-y-3 mb-4 max-h-96 overflow-y-auto" data-testid="comment-list">{items.map((c) => (
          <li key={c.id} className={`rounded-lg p-3 text-sm ${c.author_role === "grazier" ? "bg-amber-50 border border-amber-100" : "bg-emerald-50 border border-emerald-100"}`} data-testid={`comment-${c.id}`}>
            <p className="text-xs text-slate-500 mb-1"><span className="font-semibold text-slate-700">{c.author_name}</span> · {c.author_role === "grazier" ? "Grazier" : "TCG Grazing"} · {nzDateTime(c.at)}{c.animal_label && <> · animal <span className="font-semibold">{c.animal_label}</span></>}</p>
            <p className="whitespace-pre-wrap">{c.text}</p>
          </li>))}</ul>)}
      <div className="space-y-2">
        <textarea className="field-input min-h-[80px]" placeholder="Write a comment…" value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input" />
        <div className="flex flex-wrap gap-2 items-center">
          {animals.length > 0 && <select className="field-input max-w-[240px]" value={animalId} onChange={(e) => setAnimalId(e.target.value)} data-testid="comment-animal-select">
            <option value="">About the whole mob</option>{animals.filter((a) => a.status === "active").map((a) => <option key={a.id} value={a.id}>Animal {animalLabel(a)}</option>)}
          </select>}
          <Btn onClick={send} disabled={!text.trim() || busy} data-testid="comment-send-button"><PaperPlaneRight size={16} />Post</Btn>
        </div>
      </div>
    </Section>
  );
}
